import { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react'

import produce from 'immer'
import { Observable, EmptyError, merge, EMPTY, Subscription } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { isArray } from 'libraries/lodash'
import debug from 'libraries/front-end-core/core/debug'

const logError = (e: Error): typeof EMPTY => {
  (!(e instanceof EmptyError)) && debug.error(e)
  return EMPTY
}
// TODO: remove our dependence on this hook
const useBaseState = <T extends object>(stateSource: Observable<T>, initialState: T, deps: any[] = []): { baseState: T, setBaseState: Dispatch<SetStateAction<T>> } => {
  const [baseState, setBaseState] = useState<T>(initialState)
  const subscription = useRef<Subscription | null>(null)

  if (isArray(stateSource)) {
    stateSource = merge(...stateSource) as Observable<T>
  }

  useEffect(() => {
    if (stateSource instanceof Observable) {
      subscription.current = stateSource
        .pipe(
          catchError(logError)
        )
        .subscribe((s: T) => {
          setBaseState(prevState => produce(prevState, draft => {
            Object.entries(s).forEach(([key, value]) => {
              (draft as any)[key] = value // FIX ME
            })
          }))
        })
    }
    // Without this, a stateSource identity change (deps) re-subscribes
    // without ever tearing down the previous subscription - it's silently
    // orphaned rather than unsubscribed, and keeps calling setBaseState for
    // the rest of the component's lifetime. With multiple stale
    // subscriptions all alive at once, whichever resolves last "wins",
    // regardless of which one is actually current - confirmed as the root
    // cause of a real race (SelectedHeader.js's share-link state
    // occasionally reflecting a stale/superseded response instead of the
    // latest one).
    return () => subscription.current?.unsubscribe()
  }, [...deps])

  return { baseState, setBaseState }
}

export default useBaseState
